import { CompareMode, PresetKey } from '../components/DateRangeControl';
import { FilterState } from '../components/FilterBar';
import { getActiveBearerToken } from '../../../utils/apiCaller';

/**
 * Saved views — named filter+range combinations, persisted in localStorage.
 *
 * Per-browser only. Backend persistence is a separate enhancement; the
 * schema below is versioned so a future migration to a remote store can
 * read the same shape.
 *
 * The storage key is scoped to the authenticated user (see storageKey).
 * Saved views embed tenant-specific client/city/responsibility ids, so a
 * single shared key would leak one tenant's views into another on a shared
 * browser or during impersonation.
 */
export interface SavedView {
    id: string;
    name: string;
    preset: PresetKey;
    customFrom?: string; // YYYY-MM-DD
    customTo?: string;
    compareMode: CompareMode;
    compareCustomFrom?: string; // YYYY-MM-DD
    compareCustomTo?: string;
    filters: FilterState;
    createdAt: string;   // ISO timestamp
}

const STORAGE_PREFIX = 'av2.views.v1';

/**
 * Per-user key part derived from the active bearer token's `sub` claim (the
 * Passport user id — stable across logins; an impersonation token carries the
 * impersonated user's sub, keeping views isolated to that tenant). Falls back
 * to 'anon' when no/invalid token, so a logged-out context never collides
 * with a real user's views. Decodes inline (global base64url replace) rather
 * than via utils/jwtToken.parseJwt, whose single-replace can mis-decode.
 */
function userKeyPart(): string {
    const token = getActiveBearerToken();
    if (!token) return 'anon';
    try {
        const payload = token.split('.')[1];
        if (!payload) return 'anon';
        const decoded = JSON.parse(
            window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
        );
        const sub = decoded?.sub;
        return sub !== undefined && sub !== null && sub !== '' ? `u${sub}` : 'anon';
    } catch {
        return 'anon';
    }
}

function storageKey(): string {
    return `${STORAGE_PREFIX}.${userKeyPart()}`;
}

function safeRead(): SavedView[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(storageKey());
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Defensive: drop entries that don't match shape.
        return parsed.filter(
            (v) => v && typeof v.id === 'string' && typeof v.name === 'string',
        );
    } catch {
        return [];
    }
}

function safeWrite(views: SavedView[]): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(storageKey(), JSON.stringify(views));
    } catch {
        // QuotaExceeded — silently drop. Saved views are non-critical.
    }
}

export function listViews(): SavedView[] {
    return safeRead();
}

export function addView(view: Omit<SavedView, 'id' | 'createdAt'>): SavedView {
    const full: SavedView = {
        ...view,
        id: `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
    };
    const next = [...safeRead(), full];
    safeWrite(next);
    return full;
}

export function removeView(id: string): SavedView[] {
    const next = safeRead().filter((v) => v.id !== id);
    safeWrite(next);
    return next;
}
